import { pushApi } from "@/api/push.ts";

export type PushSupport = "ok" | "unsupported" | "insecure" | "install";

export const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
export const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || (navigator as unknown as { standalone?: boolean }).standalone === true;

/** Why push is (not) available here. Order matters: over plain http Safari hides the push APIs altogether,
 *  and on iPhone/iPad they only exist inside an app added to the Home Screen, so both are named before "unsupported". */
export function pushSupport(): PushSupport {
  if (!window.isSecureContext) return "insecure";
  if (isIos() && !isStandalone()) return "install";
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return "unsupported";
  return "ok";
}

/** Development only: the HTTPS twin of this dev site (scripts/dev-certs.sh + `web-https` on 5443). */
export function devHttpsUrl(): string | null {
  if (!import.meta.env.DEV || location.protocol === "https:") return null;
  return `https://${location.hostname}:5443${location.pathname}`;
}

function b64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function registerSw(): Promise<ServiceWorkerRegistration | null> {
  if (pushSupport() !== "ok") return null;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
    await navigator.serviceWorker.ready;
    return reg;
  } catch {
    return null;
  }
}

/** The browser's current subscription, if this device is subscribed. */
export async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await registerSw();
  return reg ? reg.pushManager.getSubscription() : null;
}

function deviceLabel(): string {
  const ua = navigator.userAgent;
  const os = /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /Mac/.test(ua) ? "macOS" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "";
  const br = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : /Firefox\//.test(ua) ? "Firefox" : "Browser";
  return [br, os].filter(Boolean).join(" · ");
}

/** Asks permission, subscribes this browser and registers the subscription with the server. */
export async function enablePush(): Promise<"granted" | "denied" | "failed"> {
  const reg = await registerSw();
  if (!reg) return "failed";
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return "denied";
  const { publicKey } = await pushApi.vapid();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(publicKey) });
  await pushApi.subscribe(sub.toJSON() as PushSubscriptionJSON, deviceLabel());
  return "granted";
}

export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (!sub) return;
  await pushApi.unsubscribeEndpoint(sub.endpoint).catch(() => undefined);
  await sub.unsubscribe();
}
